import React, { forwardRef, useCallback, useEffect, useState } from "react";
import { SearchBox, SearchBoxProps } from "../SearchBox";
import { Button } from "UIKit/Button";
import { ClearIcon, SearchMonoIcon } from "Assets";

interface ClickSearchBoxProps extends SearchBoxProps {
  onClickSearch: () => void;
  onClearSearch: () => void;
  isLoading?: boolean;
  searchButtonText?: string;
  minInputLength?: number;
}

export const ClickSearchBox = forwardRef<HTMLInputElement, ClickSearchBoxProps>(
  (
    {
      onClickSearch,
      isLoading,
      searchButtonText = "Search",
      onClearSearch,
      className,
      minInputLength = 2,
      value,
      ...otherProps
    },
    ref,
  ) => {
    const [isTypingSearch, setIsTypingSearch] = useState<boolean>(false);
    const [isSearchEnabled, setIsSearchEnabled] = useState<boolean>(false);

    const handlePressEnter = useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter") {
          e.preventDefault();
          onClickSearch();
        }
      },
      [onClickSearch],
    );

    const handleClearSearch = useCallback(() => {
      onClearSearch();
    }, [onClearSearch]);

    useEffect(() => {
      setIsTypingSearch(!!value && value.length > 0);
      setIsSearchEnabled(!!value && value.length >= minInputLength);
    }, [value, minInputLength]);

    const rightSection = (
      <div className="flex flex-row items-center h-10 sm:h-14">
        {isTypingSearch && (
          <button
            onClick={handleClearSearch}
            data-testid="clear-search"
            className="sm:mx-2 flex items-center pl-4 text-sm"
          >
            <ClearIcon />
          </button>
        )}

        <Button
          autoFocus
          onClick={onClickSearch}
          isLoading={isLoading}
          data-testid="searchbar-button"
          disabled={!isSearchEnabled}
          className={`ml-1 px-3 sm:px-8 h-10 rounded-xl ${
            isSearchEnabled ? "bg-blue-500" : "bg-grey-400 border-grey-400"
          }`}
        >
          <span className="hidden sm:inline">{searchButtonText}</span>
          <SearchMonoIcon className="h-6 w-6 inline sm:hidden" />
        </Button>
      </div>
    );

    return (
      <SearchBox
        {...otherProps}
        ref={ref}
        onKeyDown={handlePressEnter}
        rightSection={rightSection}
        className={`pr-1 ${className}`}
        value={value}
        debounceTimeout={0}
      />
    );
  },
);
